import { useEffect, useRef, useState } from "react";
import { Play, Pause, Volume2, VolumeX } from "lucide-react";
import abTracks from "../../data/abTracks";

/**
 * A/B AUDIO PLAYER — Infinagon Recordings
 *
 * To add or modify the tracks shown in this player, edit:
 *   /app/frontend/src/data/abTracks.js
 *
 * This component reads from that file. Nothing else needs to change.
 */

// Static waveform bar heights — deterministic so two stacked waveforms stay aligned
const BAR_COUNT = 72;
const BAR_HEIGHTS = Array.from({ length: BAR_COUNT }, (_, i) => {
  const v =
    (Math.sin(i * 0.6) + Math.cos(i * 0.27) * 0.8 + Math.sin(i * 1.3) * 0.6) *
      0.35 +
    0.55;
  return Math.max(0.18, Math.min(1, v));
});

const formatTime = (s) => {
  if (!isFinite(s) || s < 0) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

export const ABPlayer = () => {
  const [activeIdx, setActiveIdx] = useState(0);
  const [mode, setMode] = useState("before"); // 'before' | 'after'
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.85);
  const [muted, setMuted] = useState(false);
  const [loading, setLoading] = useState(false);

  const beforeRef = useRef(null);
  const afterRef = useRef(null);
  const activeTrack = abTracks[activeIdx];

  // Keep volume / mute in sync across both audio elements
  useEffect(() => {
    [beforeRef.current, afterRef.current].forEach((a) => {
      if (!a) return;
      a.volume = volume;
      a.muted = muted;
    });
  }, [volume, muted, activeIdx]);

  // When track changes: stop, reset
  useEffect(() => {
    setPlaying(false);
    setProgress(0);
    setDuration(0);
    [beforeRef.current, afterRef.current].forEach((a) => {
      if (a) {
        a.pause();
        a.currentTime = 0;
      }
    });
  }, [activeIdx]);

  // Progress updater
  useEffect(() => {
    let raf;
    const tick = () => {
      const a = mode === "before" ? beforeRef.current : afterRef.current;
      if (a && !isNaN(a.duration)) {
        setProgress(a.currentTime);
        setDuration(a.duration);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [mode]);

  const togglePlay = async () => {
    const a = mode === "before" ? beforeRef.current : afterRef.current;
    const other = mode === "before" ? afterRef.current : beforeRef.current;
    if (!a) return;
    if (playing) {
      a.pause();
      other?.pause();
      setPlaying(false);
    } else {
      try {
        setLoading(true);
        other?.pause();
        await a.play();
        setPlaying(true);
      } catch (e) {
        console.error("Audio play failed", e);
      } finally {
        setLoading(false);
      }
    }
  };

  const switchMode = async (next) => {
    if (next === mode) return;
    const current = mode === "before" ? beforeRef.current : afterRef.current;
    const target = next === "before" ? beforeRef.current : afterRef.current;
    if (!current || !target) return;
    const t = current.currentTime;
    target.currentTime = t;
    if (playing) {
      current.pause();
      try {
        await target.play();
      } catch (e) {
        console.error(e);
      }
    }
    setMode(next);
  };

  const seek = (pct) => {
    const a = mode === "before" ? beforeRef.current : afterRef.current;
    if (!a || !a.duration) return;
    const t = pct * a.duration;
    a.currentTime = t;
    const other = mode === "before" ? afterRef.current : beforeRef.current;
    if (other && !isNaN(other.duration)) other.currentTime = t;
  };

  const pct = duration > 0 ? progress / duration : 0;

  return (
    <section
      id="ab-player"
      data-testid="ab-player-section"
      className="relative bg-black text-white border-t-2 border-black"
    >
      <div className="px-6 md:px-12 lg:px-20 py-20 md:py-28">
        <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <div>
            <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-white/60 mb-4">
              [ 003 / Before · After ]
            </p>
            <h2 className="font-display uppercase leading-[0.85] tracking-tighter text-5xl sm:text-6xl md:text-7xl">
              Listen
            </h2>
          </div>
          <p className="max-w-md text-white/70">
            Toggle between raw and finished to hear the craft. Same moment,
            two worlds. Pick a track from the right.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 border-2 border-white">
          {/* Left: player */}
          <div className="lg:col-span-8 p-6 md:p-10 border-b-2 lg:border-b-0 lg:border-r-2 border-white">
            {/* Track info */}
            <div className="flex items-start justify-between gap-6 flex-wrap">
              <div>
                <div className="font-mono text-[11px] tracking-[0.25em] uppercase text-white/60 mb-3">
                  {activeTrack.category} · {activeTrack.year}
                </div>
                <div className="font-display text-4xl md:text-5xl uppercase tracking-tighter leading-none">
                  {activeTrack.title}
                </div>
                <div className="mt-3 text-white/70">
                  {activeTrack.artist}
                </div>
                {activeTrack.notes && (
                  <div className="mt-4 text-sm text-white/55 max-w-lg">
                    {activeTrack.notes}
                  </div>
                )}
              </div>

              {/* A/B toggle */}
              <div
                data-testid="ab-toggle"
                className="inline-flex border-2 border-white p-0 font-mono text-xs tracking-[0.25em] uppercase self-start"
              >
                <button
                  data-testid="ab-toggle-before"
                  onClick={() => switchMode("before")}
                  className={`px-6 py-3 transition-colors ${
                    mode === "before" ? "bg-white text-black" : "bg-transparent text-white hover:bg-white/10"
                  }`}
                >
                  A · Before
                </button>
                <button
                  data-testid="ab-toggle-after"
                  onClick={() => switchMode("after")}
                  className={`px-6 py-3 transition-colors border-l-2 border-white ${
                    mode === "after" ? "bg-white text-black" : "bg-transparent text-white hover:bg-white/10"
                  }`}
                >
                  B · After
                </button>
              </div>
            </div>

            {/* Waveform visualization */}
            <div className="mt-10 relative">
              <div
                data-testid="ab-waveform"
                className="relative h-28 md:h-36 flex items-center gap-[3px] cursor-pointer select-none"
                onClick={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const p = (e.clientX - rect.left) / rect.width;
                  seek(Math.max(0, Math.min(1, p)));
                }}
              >
                {BAR_HEIGHTS.map((h, i) => {
                  const played = i / BAR_HEIGHTS.length <= pct;
                  return (
                    <div
                      key={i}
                      style={{
                        height: `${h * 100}%`,
                        animationDelay: `${i * 25}ms`,
                      }}
                      className={`flex-1 ${playing && Math.abs(i / BAR_HEIGHTS.length - pct) < 0.04 ? "wave-bar" : ""} ${
                        played ? "bg-white" : "bg-white/25"
                      }`}
                    />
                  );
                })}
              </div>
              <div className="mt-3 flex justify-between font-mono text-[11px] tracking-[0.2em] text-white/60">
                <span data-testid="ab-time-current">{formatTime(progress)}</span>
                <span data-testid="ab-mode-indicator">
                  Mode: {mode === "before" ? "A · BEFORE (RAW)" : "B · AFTER (MIXED)"}
                </span>
                <span data-testid="ab-time-duration">{formatTime(duration)}</span>
              </div>
            </div>

            {/* Transport controls */}
            <div className="mt-8 flex items-center justify-between flex-wrap gap-6">
              <button
                data-testid="ab-play-btn"
                onClick={togglePlay}
                className="flex items-center gap-4 bg-white text-black px-8 py-5 border-2 border-white hover:bg-black hover:text-white transition-colors font-mono text-xs tracking-[0.25em] uppercase"
              >
                {loading ? (
                  <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                ) : playing ? (
                  <Pause size={18} fill="currentColor" />
                ) : (
                  <Play size={18} fill="currentColor" />
                )}
                {playing ? "Pause" : "Play"}
              </button>

              <div className="flex items-center gap-3 min-w-[180px]">
                <button
                  data-testid="ab-mute-btn"
                  onClick={() => setMuted((m) => !m)}
                  className="p-2 border-2 border-white/50 hover:border-white"
                >
                  {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
                </button>
                <input
                  data-testid="ab-volume"
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={muted ? 0 : volume}
                  onChange={(e) => {
                    setMuted(false);
                    setVolume(parseFloat(e.target.value));
                  }}
                  className="flex-1 accent-white h-1"
                />
              </div>
            </div>

            {/* Hidden audio elements — preload="auto" so flipping
                A/B is instant once the active track is loaded. */}
            <audio
              ref={beforeRef}
              src={activeTrack.before}
              preload="auto"
              onEnded={() => setPlaying(false)}
              data-testid="ab-audio-before"
            />
            <audio
              ref={afterRef}
              src={activeTrack.after}
              preload="auto"
              onEnded={() => setPlaying(false)}
              data-testid="ab-audio-after"
            />
          </div>

          {/* Right: tracklist */}
          <div className="lg:col-span-4 bg-white text-black">
            <div className="px-6 md:px-8 py-5 border-b-2 border-black flex items-center justify-between">
              <span className="font-mono text-[11px] tracking-[0.25em] uppercase">
                Tracklist
              </span>
              <span className="font-mono text-[11px] tracking-[0.25em] uppercase text-black/50">
                {abTracks.length.toString().padStart(2, "0")} / {abTracks.length.toString().padStart(2, "0")}
              </span>
            </div>
            <ul
              data-testid="ab-tracklist"
              className="max-h-[520px] overflow-y-auto divide-y divide-black/10"
            >
              {abTracks.map((t, i) => {
                const active = i === activeIdx;
                return (
                  <li key={t.id}>
                    <button
                      data-testid={`ab-track-${t.id}`}
                      onClick={() => setActiveIdx(i)}
                      className={`w-full text-left px-6 md:px-8 py-5 flex items-start gap-4 transition-colors ${
                        active ? "bg-black text-white" : "hover:bg-black/5"
                      }`}
                    >
                      <span className={`font-mono text-[11px] tracking-[0.25em] pt-1 ${active ? "text-white/60" : "text-black/40"}`}>
                        {(i + 1).toString().padStart(2, "0")}
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block font-display text-xl uppercase tracking-tight truncate">
                          {t.title}
                        </span>
                        <span className={`block text-sm mt-0.5 truncate ${active ? "text-white/75" : "text-black/65"}`}>
                          {t.artist} · {t.category}
                        </span>
                      </span>
                      {active && playing && (
                        <span className="flex gap-[2px] h-4 items-end">
                          {[0, 1, 2].map((b) => (
                            <span
                              key={b}
                              className="w-[3px] bg-white wave-bar"
                              style={{ animationDelay: `${b * 120}ms`, height: "100%" }}
                            />
                          ))}
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
            {/* NOTE TO OWNER (hidden from the rendered page):
                To add more A/B tracks, edit /src/data/abTracks.js
                A commented-out copy/paste template lives at the
                top of that file. */}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ABPlayer;
